/**
 * Shared card trend block — the chart-type dropdown + mini-chart + rolling
 * average that EVERY card gets, not just the headline KPIs (founder 2026-06-13:
 * "you updated the REVENUE card with the chart dropdown… why didn't you do that
 * for ALL cards? Our recent changes have to apply to ALL cards").
 *
 * The series comes from the SAME period-aware /dashboard/breakdown endpoint the
 * drilldowns use, so a card's mini-trend and its drill reconcile, and both move
 * when the global timeline changes. Daily granularity for short windows
 * (30/90d), monthly for long ones, so the chart is legible either way.
 *
 * A card calls useCardChart({metric|trend, chartKey}) and drops `menu` into its
 * CardHead and `body` into its card body. Cards whose metric has no honest time
 * series (ratios like MER/CAC, lifetime cohorts) simply pass no metric and get
 * {menu:null, body:null} — number-only, by design.
 */

import { useCallback, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useApiClient } from "../lib/api-context.js";
import { usePeriod } from "../lib/period.js";
import { moneyFmt } from "../lib/format.js";
import { ChartTypeMenu, useChartPref, rollingAverage, rollingWindow } from "./ChartTypeMenu.js";
import type { DrillMetric } from "./DrillExplorer.js";

export interface TrendPt { label: string; value: number }

/** How each breakdown metric formats — mirrors the backend METRIC_META so a
 *  card only has to pass its metric and the chart formats itself (money / count
 *  / percent). Keep in sync with DrillExplorer's METRIC_META. */
const METRIC_FMT: Partial<Record<DrillMetric, { money?: boolean; percent?: boolean }>> = {
  revenue: { money: true }, cm: { money: true }, product_revenue: { money: true }, product_cm: { money: true }, aov: { money: true }, cm_aov: { money: true }, refunded_amount: { money: true }, rebill_revenue: { money: true }, rebill_cm: { money: true },
  orders: {}, customers: {}, new_customers: {}, units: {}, boxes: {}, refunded_orders: {}, subs_new: {}, subs_canceled: {}, subs_ended: {},
  refund_rate: { percent: true },
  chargeback_amount: { money: true }, chargeback_count: {}, chargeback_rate: { percent: true },
};

export const CARD_CHART_MENU = [
  { value: "number", label: "Number" }, { value: "line", label: "Line" },
  { value: "area", label: "Area" }, { value: "bars", label: "Bars" }, { value: "table", label: "Table" },
];

/** Older full-size charts saved a different spelling under shared card keys. */
export function cardChartType(value: string): string {
  if (["stacked-bars", "grouped-bars", "column"].includes(value)) return "bars";
  return CARD_CHART_MENU.some((option) => option.value === value) ? value : "line";
}

/** Metrics that have an honest /dashboard/breakdown time series (so a card can
 *  chart them). Ratios/derived metrics with no per-period series are absent on
 *  purpose. Lets a Tile auto-derive its trend from its drill metric. */
const CHARTABLE = new Set<string>(Object.keys(METRIC_FMT));
export function isChartableMetric(m: string | undefined): m is DrillMetric {
  return !!m && CHARTABLE.has(m);
}

/** Fetch a metric's time series from the period-scoped breakdown endpoint.
 *  Daily for 30/90d windows, monthly otherwise; sorted chronologically. */
export function useMetricTrend(metric: DrillMetric | undefined, enabled = true): TrendPt[] | undefined {
  const api = useApiClient();
  const period = usePeriod();
  const daily = period.key === "30d" || period.key === "90d";
  const q = useQuery({
    queryKey: ["card-trend", metric, period.cacheKey, daily ? "day" : "month"],
    enabled: enabled && !!metric,
    staleTime: 60_000,
    queryFn: () =>
      api.get<{ rows: Array<{ key: string; value: number }> }>("/dashboard/breakdown", {
        metric: metric as string,
        dimension: "month",
        ...period.query,
        ...(daily ? { granularity: "day" } : {}),
      }),
  });
  return q.data?.rows
    .map((r) => ({ label: r.key, value: r.value }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/** Blended marketing ratios (MER / aMER / CAC). These are two period totals
 *  divided, so they have no per-order breakdown row — the series comes from the
 *  dedicated /dashboard/ratio-trend endpoint (numerator ÷ denominator per
 *  month). Founder 2026-06-14: show MER by month once the window is > 30 days. */
export type RatioKey = "mer" | "amer" | "cac" | "spend" | "cm-mer" | "cm-amer";

/** Fetch a blended ratio's MONTHLY series. Always monthly (a ratio reads by
 *  month); the caller gates it (only show it for windows longer than 30 days). */
export function useRatioTrend(ratio: RatioKey | undefined, enabled = true): TrendPt[] | undefined {
  const api = useApiClient();
  const period = usePeriod();
  // Shared query key (NOT per-ratio) so the six KPI cards' calls dedupe into ONE
  // /ratio-trends request that returns all series at once — the six separate
  // /ratio-trend calls used to queue behind the browser's connection limit and
  // slow the page load (founder 2026-07-06). The server computes the expensive
  // ratios sequentially for reliability; each card reads its own slice below.
  const q = useQuery({
    queryKey: ["ratio-trends", period.cacheKey],
    enabled: enabled && !!ratio,
    staleTime: 60_000,
    queryFn: () =>
      api.get<{ series: Record<string, Array<{ key: string; value: number | null; incomplete?: boolean }>> }>(
        "/dashboard/ratio-trends",
        { ...period.query },
      ),
  });
  if (!q.data || !ratio) return undefined;
  const rows = q.data.series[ratio] ?? [];
  // M3: drop the still-incomplete current month so the trend doesn't nosedive
  // every month-end — but never blank the chart (keep it if it's all we have).
  const measurable = rows.filter((r): r is typeof r & { value: number } => r.value !== null);
  const complete = measurable.filter((r) => !r.incomplete);
  const shown = complete.length > 0 ? complete : measurable;
  return shown
    .map((r) => ({ label: r.key, value: r.value }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/** The dropdown (for the CardHead) + the chart (for the card body). Rolling
 *  average defaults ON via useChartPref. */
export function useCardChart({
  trend: explicitTrend,
  metric,
  chartKey,
  money,
  percent,
  fmt: fmtOverride,
  defaultChart = "line",
  enabled = true,
}: {
  /** Pass an already-fetched series (e.g. the KPI daily series) … */
  trend?: TrendPt[];
  /** … or a metric to fetch the series for. */
  metric?: DrillMetric;
  chartKey: string;
  /** Override the metric's default formatting (money/percent auto-derived). */
  money?: boolean;
  percent?: boolean;
  /** Full formatter override (e.g. "4.2×" for a ratio) — wins over money/percent. */
  fmt?: (n: number) => string;
  /** First-load chart type when the user hasn't picked one. Ratio cards default
   *  to "table" (founder 2026-06-14: "anything beyond 30 days I'd show the table"). */
  defaultChart?: string;
  enabled?: boolean;
}): { menu: React.ReactNode | null; body: React.ReactNode | null; hasTrend: boolean; ref: React.RefCallback<HTMLDivElement> } {
  // Lazy-load: a fetched-metric card only queries once it scrolls into view, so
  // a page with dozens of cards (the Card Library) doesn't fire dozens of
  // breakdown queries at once (Codex 2026-06-13 perf review). A CALLBACK ref
  // wires the observer exactly when the card's node mounts — robust even if the
  // card starts as a locked/placeholder and the real node appears later. Cards
  // with an explicit in-memory trend (KPI daily series) skip the gate.
  const [seen, setSeen] = useState(false);
  const ioRef = useRef<IntersectionObserver | null>(null);
  const ref = useCallback((node: HTMLDivElement | null) => {
    if (ioRef.current) { ioRef.current.disconnect(); ioRef.current = null; }
    if (!node || explicitTrend) return;
    if (typeof IntersectionObserver === "undefined") { setSeen(true); return; }
    const io = new IntersectionObserver(
      (entries) => { if (entries.some((e) => e.isIntersecting)) { setSeen(true); io.disconnect(); ioRef.current = null; } },
      { rootMargin: "250px" },
    );
    io.observe(node);
    ioRef.current = io;
  }, [explicitTrend]);
  const fetched = useMetricTrend(metric, enabled && !explicitTrend && seen);
  const trend = explicitTrend ?? fetched;
  const pref = useChartPref(chartKey, defaultChart);
  const hasTrend = !!trend && trend.length >= 3;
  const view = hasTrend ? cardChartType(pref.type) : "number";
  const isChart = view === "line" || view === "area" || view === "bars";
  const f = metric ? METRIC_FMT[metric] : undefined;
  // A metric-driven card formats by its METRIC (units → counts, revenue → $).
  // Only fall back to money when there's no metric AND no explicit flag — the
  // old `money = true` default was masking every count metric as dollars.
  const isMoney = money ?? f?.money ?? (metric ? false : true);
  const isPercent = percent ?? f?.percent ?? false;
  const fmt = fmtOverride ?? ((n: number): string => (isPercent ? `${n.toFixed(1)}%` : isMoney ? moneyFmt(n) : Math.round(n).toLocaleString()));
  const menu = hasTrend ? (
    <ChartTypeMenu value={view} options={CARD_CHART_MENU} onChange={pref.setType} avg={pref.avg} onToggleAvg={pref.setAvg} avgApplies={isChart} />
  ) : null;
  const body = !hasTrend || !trend
    ? null
    : view === "table"
      ? <MiniTable trend={trend} fmt={fmt} />
      : isChart
        ? <Spark values={trend.map((t) => t.value)} kind={view} avg={pref.avg} />
        : null;
  return { menu, body, hasTrend, ref };
}

/** Tiny inline trend (line / area / bars) with an optional rolling-average
 *  overlay (dashed --accent-2). Fluid width, fixed height. */
export function Spark({ values, kind, avg }: { values: number[]; kind: "line" | "area" | "bars"; avg?: boolean }): React.ReactElement | null {
  if (values.length < 3) return null;
  const W = 220, H = 34;
  const max = Math.max(1, ...values);
  const px = (i: number): number => (kind === "bars" ? (i + 0.5) * (W / values.length) : (i / (values.length - 1)) * W);
  const py = (v: number): number => H - 3 - (v / max) * (H - 6);
  const overlay = ((): React.ReactNode => {
    if (!avg) return null;
    const ma = rollingAverage(values, rollingWindow(values.map(() => "d")));
    const segs = ma.map((v, i) => (v === null ? null : `${px(i).toFixed(1)},${py(v).toFixed(1)}`)).filter((s): s is string => s !== null);
    return segs.length >= 2 ? <path d={`M${segs.join(" L")}`} fill="none" stroke="var(--accent-2)" strokeWidth="1.6" strokeDasharray="4 3" strokeLinejoin="round" /> : null;
  })();
  if (kind === "bars") {
    const bw = W / values.length;
    return (
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} preserveAspectRatio="none" style={{ display: "block", marginTop: 8, opacity: 0.9 }}>
        {values.map((v, i) => {
          const h = Math.max(1.5, (v / max) * (H - 4));
          return <rect key={i} x={(i * bw + 0.6).toFixed(1)} y={(H - h).toFixed(1)} width={Math.max(1, bw - 1.2).toFixed(1)} height={h.toFixed(1)} rx={1} fill="var(--accent)" />;
        })}
        {overlay}
      </svg>
    );
  }
  const line = `M${values.map((v, i) => `${px(i).toFixed(1)},${py(v).toFixed(1)}`).join(" L")}`;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} preserveAspectRatio="none" style={{ display: "block", marginTop: 8, opacity: 0.9 }}>
      {kind === "area" && (
        <>
          <defs><linearGradient id="kpi-spark-grad" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="var(--accent)" stopOpacity="0.25" /><stop offset="100%" stopColor="var(--accent)" stopOpacity="0" /></linearGradient></defs>
          <path d={`${line} L${W},${H} L0,${H} Z`} fill="url(#kpi-spark-grad)" />
        </>
      )}
      <path d={line} fill="none" stroke="var(--accent)" strokeWidth="1.6" strokeLinejoin="round" />
      {overlay}
    </svg>
  );
}

/** "MM/DD" from a daily "YYYY-MM-DD" label, "Mon YYYY" from a monthly "YYYY-MM"
 *  label (string slice — no Date parsing). */
const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
function shortDay(label: string): string {
  const d = /^\d{4}-(\d{2})-(\d{2})$/.exec(label);
  if (d) return `${d[1]}/${d[2]}`;
  const m = /^(\d{4})-(\d{2})$/.exec(label);
  if (m) return `${MON[Number(m[2]) - 1]} ${m[1]}`;
  return label;
}

/** Series as a compact scrollable table (newest first). Capped to the most
 *  recent 90 rows so a long series doesn't bloat a small card — the full
 *  history lives in the drill. */
export function MiniTable({ trend, fmt }: { trend: TrendPt[]; fmt: (n: number) => string }): React.ReactElement {
  const recent = [...trend].reverse().slice(0, 90);
  const truncated = trend.length > 90;
  return (
    <div style={{ maxHeight: 132, overflowY: "auto", marginTop: 8, border: "1px solid var(--border)", borderRadius: 6 }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11.5 }}>
        <tbody>
          {recent.map((r) => (
            <tr key={r.label} style={{ borderTop: "1px solid var(--border)" }}>
              <td style={{ padding: "3px 8px", color: "var(--body)" }}>{shortDay(r.label)}</td>
              <td className="dd-num" style={{ padding: "3px 8px", textAlign: "right", color: "var(--num)" }}>{fmt(r.value)}</td>
            </tr>
          ))}
          {truncated && (
            <tr><td colSpan={2} style={{ padding: "4px 8px", color: "var(--faint)", fontSize: 10.5 }}>last 90 · open the card to drill the rest</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
